import React from 'react';
import { FileUploadContext } from 'components/FileUpload';

import VirtualUploadIcon from '@nokia-csf-uxr/ccfk-assets/VirtualUploadIcon';
import ArrowsContractIcon from '@nokia-csf-uxr/ccfk-assets/ArrowsContractIcon';
import AlarmCircleIcon from '@nokia-csf-uxr/ccfk-assets/AlarmCircleIcon';
import Styled from './styles';

export interface FileUploadIconProps extends React.ComponentPropsWithRef<'div'> {
  /** Content of this FileUploadIcon. If set, the content of iconNormal and dragActiveIcon will not be rendered. */
  children?: React.ReactNode;
  /** The function which render the normal icon of the `FileUploadIcon`. */
  iconNormal?: React.ReactNode;
  /** The function which render the icon when dragging is active */
  dragActiveIcon?: React.ReactNode;
}

/** Single icon wrapper for FileUploadIcon.
 * Any other props will be provided to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.)
 */
const FileUploadIcon = React.forwardRef<HTMLElement, FileUploadIconProps>((props: FileUploadIconProps, ref) => {
  const {
    children,
    iconNormal,
    dragActiveIcon,
    ...otherProps
  } = props;
  const { isDragActive, error, $theme } = React.useContext(FileUploadContext);

  const content = () => {
    if (children) {
      return children;
    }
    if (!isDragActive) {
      return iconNormal;
    }
    if (error) {
      return <AlarmCircleIcon color={$theme.onDrop.error.icon} />;
    }
    return dragActiveIcon || <ArrowsContractIcon color={$theme.onDrop.icon} />;
  };
  return (
    <Styled.IconWrapper ref={ref} {...otherProps}>
      {content()}
    </Styled.IconWrapper>
  );
});
FileUploadIcon.displayName = 'FileUploadIcon';

FileUploadIcon.defaultProps = {
  children: undefined,
  iconNormal: <VirtualUploadIcon />,
  dragActiveIcon: undefined
};

export default FileUploadIcon;
